/**
 * Piece Definitions Module
 *
 * Defines all tetromino piece shapes (T, S, O, L, bonus) with their default
 * orientations and metadata. Each piece shape is defined as a set of relative
 * coordinates from a reference point (0,0).
 *
 * Coordinate system:
 * - X axis: horizontal (0 = leftmost)
 * - Y axis: vertical (0 = topmost)
 * - All coordinates are relative to top-left corner of piece's bounding box
 */

/**
 * Piece type constants
 */
export const PIECE_TYPES = {
  T: 'T',
  S: 'S',
  O: 'O',
  L: 'L',
  BONUS: 'bonus',
};

/**
 * Piece metadata
 */
export const PIECE_METADATA = {
  [PIECE_TYPES.T]: {
    name: 'T-Piece',
    color: '#26f4de', // neon-cyan
    size: 4,
  },
  [PIECE_TYPES.S]: {
    name: 'S-Piece',
    color: '#4aff28', // neon-green
    size: 4,
  },
  [PIECE_TYPES.O]: {
    name: 'O-Piece',
    color: '#ffff0f', // neon-yellow
    size: 4,
  },
  [PIECE_TYPES.L]: {
    name: 'L-Piece',
    color: '#9b1ad9', // neon-purple
    size: 4,
  },
  [PIECE_TYPES.BONUS]: {
    name: 'Bonus Piece',
    color: '#ff10f0', // neon-pink
    size: 2,
  },
};

/**
 * Define all piece shapes in their default (0 degree) orientation
 * Each shape is an array of {x, y} coordinates relative to origin (0,0)
 *
 * Example visualization for T-piece:
 *   [1][1][1]
 *      [1]
 *
 * Becomes: [{x:0,y:0}, {x:1,y:0}, {x:2,y:0}, {x:1,y:1}]
 */
export const PIECE_SHAPES = {
  [PIECE_TYPES.T]: [
    // T-shape: horizontal bar with center extending down
    //  X X X
    //    X
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 2, y: 0 },
    { x: 1, y: 1 },
  ],

  [PIECE_TYPES.S]: [
    // S-shape: zigzag
    //    X X
    //  X X
    { x: 1, y: 0 },
    { x: 2, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
  ],

  [PIECE_TYPES.O]: [
    // O-shape: 2x2 square
    //  X X
    //  X X
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
  ],

  [PIECE_TYPES.L]: [
    // L-shape: vertical bar with bottom extending right
    //  X
    //  X
    //  X X
    { x: 0, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: 2 },
    { x: 1, y: 2 },
  ],

  [PIECE_TYPES.BONUS]: [
    // Bonus: simple 2-square horizontal piece
    //  X X
    { x: 0, y: 0 },
    { x: 1, y: 0 },
  ],
};

/**
 * Get piece definition by type
 * @param {string} type - Piece type (T, S, O, L, bonus)
 * @returns {Object} Piece definition with shape and metadata
 */
export function getPieceDefinition(type) {
  const shape = PIECE_SHAPES[type];
  const metadata = PIECE_METADATA[type];

  if (!shape || !metadata) {
    throw new Error(`Unknown piece type: ${type}`);
  }

  return {
    type,
    shape,
    ...metadata,
  };
}

/**
 * Get all piece types
 * @returns {Array<string>} Array of all piece type identifiers
 */
export function getAllPieceTypes() {
  return Object.values(PIECE_TYPES);
}

/**
 * Validate piece type
 * @param {string} type - Piece type to validate
 * @returns {boolean} True if valid piece type
 */
export function isValidPieceType(type) {
  return Object.values(PIECE_TYPES).includes(type);
}
